import React, { useState } from 'react';
import { 
  Row, 
  Col, 
  Typography, 
  Collapse, 
  Card, 
  Button, 
  Input, 
  Space
} from 'antd';
import { 
  SearchOutlined,
  QuestionCircleOutlined,
  PhoneOutlined,
  MailOutlined,
  MessageOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { theme } from '../../styles/theme';
import { FAQ_CONTENT } from '../../constants/faqContent';

const { Title, Paragraph, Text } = Typography;
const { Panel } = Collapse;
const { Search } = Input;

const FAQPage = () => {
  const [searchTerm, setSearchTerm] = useState('');

  const faqCategories = FAQ_CONTENT.categories.map(category => ({
    ...category,
    icon: <QuestionCircleOutlined />
  }));

  const filteredCategories = faqCategories.map(category => ({
    ...category,
    questions: category.questions.filter(q => 
      q.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.answer.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  const contactMethods = FAQ_CONTENT.contactSupport.methods.map((method, index) => {
    const icons = [
      <PhoneOutlined style={{ fontSize: '2rem', color: theme.colors.primary }} />,
      <MailOutlined style={{ fontSize: '2rem', color: theme.colors.primary }} />,
      <MessageOutlined style={{ fontSize: '2rem', color: theme.colors.primary }} />
    ];
    return { ...method, icon: icons[index] };
  });

  return (
    <>
      <div style={{ minHeight: '100vh', marginTop: '80px' }}>
      {/* Hero Section */}
      <section style={{ 
        background: `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primaryDark} 100%)`,
        padding: '60px 0',
        color: 'white'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{ textAlign: 'center' }}>
            <Title level={1} style={{ 
              color: 'white', 
              marginBottom: '1rem',
              fontFamily: theme.fonts.primary,
              fontWeight: '700',
              fontSize: '2.5rem'
            }}>
              {FAQ_CONTENT.hero.title}
            </Title>
            <Paragraph style={{ 
              color: 'white', 
              fontSize: '1.1rem', 
              marginBottom: '2rem',
              fontFamily: theme.fonts.primary,
              fontWeight: '500'
            }}>
              {FAQ_CONTENT.hero.subtitle}
            </Paragraph>
            
            {/* Search Bar */}
            <div style={{ maxWidth: '600px', margin: '0 auto' }}>
              <Search
                placeholder={FAQ_CONTENT.hero.searchPlaceholder}
                size="large"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  borderRadius: '25px',
                  fontFamily: theme.fonts.primary
                }}
                prefix={<SearchOutlined style={{ color: theme.colors.gray }} />}
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section style={{ padding: '60px 0', background: 'white' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
          {searchTerm && (
            <div style={{ marginBottom: '2rem' }}>
              <Text style={{ fontSize: '1.1rem', color: theme.colors.gray }}>
                {FAQ_CONTENT.search.resultsText(
                  filteredCategories.reduce((total, cat) => total + cat.questions.length, 0),
                  searchTerm
                )}
              </Text>
            </div>
          )}

          {filteredCategories.length === 0 && searchTerm ? (
            <div style={{ textAlign: 'center', padding: '3rem 0' }}>
              <Title level={3}>{FAQ_CONTENT.search.noResultsTitle}</Title>
              <Paragraph style={{ fontSize: '1.1rem', color: theme.colors.gray }}>
                {FAQ_CONTENT.search.noResultsMessage}
              </Paragraph>
              <Button 
                type="primary" 
                size="large"
                onClick={() => setSearchTerm('')}
                style={{
                  background: theme.colors.primary,
                  border: 'none',
                  borderRadius: '25px',
                  height: '45px',
                  padding: '0 30px',
                  fontFamily: theme.fonts.primary,
                  fontWeight: 'bold'
                }}
              >
                {FAQ_CONTENT.search.clearButton}
              </Button>
            </div>
          ) : (
            <Row gutter={[32, 32]}>
              {filteredCategories.map((category, categoryIndex) => (
                <Col xs={24} lg={12} key={categoryIndex}>
                  <Card
                    style={{
                      height: '100%',
                      borderRadius: theme.borderRadius.lg,
                      boxShadow: theme.shadows.md,
                      border: `1px solid ${theme.colors.borderGray}`
                    }}
                    bodyStyle={{ padding: '2rem' }}
                  >
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      marginBottom: '1.5rem' 
                    }}>
                      {category.icon}
                      <Title level={3} style={{ margin: '0 0 0 1rem', color: theme.colors.primary }}>
                        {category.title}
                      </Title>
                    </div>
                    
                    <Collapse
                      ghost
                      expandIconPosition="right"
                      style={{ 
                        backgroundColor: 'transparent',
                        fontFamily: theme.fonts.primary
                      }}
                    >
                      {category.questions.map((faq, faqIndex) => (
                        <Panel
                          header={
                            <Text strong style={{ 
                              fontSize: '1.1rem',
                              color: theme.colors.black
                            }}>
                              {faq.question}
                            </Text>
                          }
                          key={faqIndex}
                          style={{
                            marginBottom: '0.5rem',
                            border: `1px solid ${theme.colors.borderGray}`,
                            borderRadius: theme.borderRadius.md
                          }}
                        >
                          <Paragraph style={{ 
                            color: theme.colors.gray,
                            fontSize: '1rem',
                            lineHeight: '1.6',
                            margin: 0
                          }}>
                            {faq.answer}
                          </Paragraph>
                        </Panel>
                      ))}
                    </Collapse>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </div>
      </section>

      {/* Contact Support Section */}
      <section style={{ 
        background: theme.colors.lightGray,
        padding: '60px 0'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <Title level={2}>{FAQ_CONTENT.contactSupport.title}</Title>
            <Paragraph style={{ fontSize: '1.2rem', color: theme.colors.gray }}>
              {FAQ_CONTENT.contactSupport.subtitle}
            </Paragraph>
          </div>

          <Row gutter={[32, 32]}>
            {contactMethods.map((method, index) => (
              <Col xs={24} md={8} key={index}>
                <Card
                  style={{
                    height: '100%',
                    textAlign: 'center',
                    borderRadius: theme.borderRadius.lg,
                    boxShadow: theme.shadows.md,
                    border: `1px solid ${theme.colors.borderGray}`,
                    transition: 'all 0.3s ease'
                  }}
                  bodyStyle={{ padding: '2rem' }}
                  hoverable
                >
                  <div style={{ marginBottom: '1.5rem' }}>
                    {method.icon}
                  </div>
                  <Title level={4} style={{ marginBottom: '0.5rem' }}>
                    {method.title}
                  </Title>
                  <Paragraph style={{ color: theme.colors.gray, marginBottom: '1rem' }}>
                    {method.description}
                  </Paragraph>
                  <Text strong style={{ 
                    display: 'block', 
                    marginBottom: '1.5rem',
                    fontSize: '1.1rem',
                    color: theme.colors.primary
                  }}>
                    {method.contact}
                  </Text>
                  <Button 
                    type="primary"
                    style={{
                      background: theme.colors.primary,
                      border: 'none',
                      borderRadius: '25px',
                      height: '40px',
                      padding: '0 24px',
                      fontFamily: theme.fonts.primary,
                      fontWeight: 'bold'
                    }}
                  >
                    {method.action}
                  </Button>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{ 
        background: `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primaryDark} 100%)`,
        padding: '60px 0',
        color: 'white'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{ textAlign: 'center' }}>
            <Title level={2} style={{ color: 'white', marginBottom: '1rem' }}>
              {FAQ_CONTENT.cta.title}
            </Title>
            <Paragraph style={{ color: 'white', fontSize: '1.2rem', marginBottom: '2rem' }}>
              {FAQ_CONTENT.cta.subtitle}
            </Paragraph>
            <Space size="large">
              <Button 
                type="primary" 
                size="large"
                style={{ 
                  background: 'white',
                  color: theme.colors.primary,
                  border: 'none',
                  borderRadius: '25px',
                  height: '50px',
                  padding: '0 30px',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  fontFamily: theme.fonts.primary
                }}
              >
                <Link to={FAQ_CONTENT.cta.primaryLink}>{FAQ_CONTENT.cta.primaryButton}</Link>
              </Button>
              <Button 
                size="large"
                style={{ 
                  background: 'transparent',
                  color: 'white',
                  border: '2px solid white',
                  borderRadius: '25px',
                  height: '50px',
                  padding: '0 30px',
                  fontSize: '1.1rem',
                  fontFamily: theme.fonts.primary
                }}
              >
                <Link to={FAQ_CONTENT.cta.secondaryLink} style={{ color: 'white' }}>{FAQ_CONTENT.cta.secondaryButton}</Link>
              </Button>
            </Space>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default FAQPage;
